import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { pushToGTM, selectTableColumnTagGA4 } from '../../utils/analytics/gtm';
import api, { API_STATES } from '../../api';
import { getUniquePropertyValues } from '../../utils/helperFunctions';
import { CIDS, COLUMN_DISPLAY, SORT_DIRECTIONS } from '../../components/ProductsTable/constants';
import { cleanHTMLtags, RenderCustomCellComponent, RenderCustomHeadLabel } from '../../components/ProductsTable/utils';

export const fetchByCategoryParam = createAsyncThunk('categoryData/fetchCategoryData', async categoryApiParam => {
	const data = await api.getCategoryData(categoryApiParam);
	return data;
});
export const evalWithWebDesigner = createAsyncThunk('categoryData/fetchCategoryData', async inputApiParam => {
	const data = await api.evalWithWebDesigner(inputApiParam);
	return data;
});

// eslint-disable-next-line no-extend-native
Array.prototype.sortByProperty = function (property) {
	return this.sort((a, b) => (a[property] > b[property] ? 1 : b[property] > a[property] ? -1 : 0));
};

const categoryDataSlice = createSlice({
	name: 'categoryData',
	initialState: {
		columns: [],
		filters: [],
		chartFilters: [],
		filterRelations: [],
		products: [],
		filteredProducts: [],
		status: API_STATES.PENDING,
		applicationOriented: true || false,
		isApplicationOrientedExpanded: false
	},
	reducers: {
		filterProducts: (state, action) => {
			state.filteredProducts = action.payload;
		},
		resetStatus: state => {
			state.status = API_STATES.PENDING;
		},
		updateColumnDisplay: (state, action) => {
			try {
				const cid = action.payload;
				const newDisplay =
					state.columns.find(col => col.cid === cid).display === COLUMN_DISPLAY.TRUE
						? COLUMN_DISPLAY.FALSE
						: COLUMN_DISPLAY.TRUE;

				pushToGTM(selectTableColumnTagGA4(state.columns, cid));

				return {
					...state,
					columns: state.columns.map(col => (col.cid === cid ? { ...col, display: newDisplay } : col))
				};
			} catch (e) {
				console.error(e);
			}
		},
		changeExpandState: (state, action) => {
			state.isApplicationOrientedExpanded = action.payload;
		}
	},
	extraReducers: {
		[fetchByCategoryParam.pending]: (state, _action) => {
			state.status = API_STATES.LOADING;
		},
		[fetchByCategoryParam.fulfilled]: (state, action) => {
			try {
				const { columns, filters, filterRelations, products, graphFilters, applicationOriented } = action.payload;

				injectToolsColumn(columns);

				const sortedColumns = columns.sortByProperty('order');
				filters.sortByProperty('order');
				products.sortByProperty('rank');

				return {
					...state,
					columns: sortedColumns,
					filters: filters,
					chartFilters: graphFilters,
					filterRelations: filterRelations,
					products: products,
					filteredProducts: products,
					status: API_STATES.SUCCESS,
					applicationOriented: applicationOriented
				};
			} catch (e) {
				console.error(e);
				state.status = API_STATES.ERROR;
			}
		},
		[fetchByCategoryParam.rejected]: (state, _action) => {
			state.status = API_STATES.ERROR;
		}
	}
});

/**
 * Ensure a "CAD Models" column (cid = 'tos') so ProductsTable can render
 * <SnapMagicViewer/> per row. Rename an existing column without duplicating it.
 * If missing, inject a synthetic column placed just after Datasheet
 * via a fractional order so the other columns' order values don't need
 * rewriting. When Datasheet is unavailable, the existing final auxiliary
 * column remains last.
 */
function injectToolsColumn(columns) {
	if (!Array.isArray(columns) || columns.length === 0) return;
	const toolsColumn = columns.find(c => c.cid === CIDS.TOOLS);
	if (toolsColumn) {
		toolsColumn.label = 'CAD Models';
		return;
	}

	const dsh = columns.find(c => c.cid === CIDS.DATASHEET);
	const finalColumn = columns[columns.length - 1];

	let order;
	if (dsh && typeof dsh.order === 'number') {
		order = dsh.order + 0.5;
	} else if (finalColumn && typeof finalColumn.order === 'number') {
		order = finalColumn.order - 0.5;
	} else {
		const maxOrder = columns.slice(0, -1).reduce((m, c) => Math.max(m, c.order || 0), 0);
		order = maxOrder + 0.5;
	}

	columns.push({
		cid: CIDS.TOOLS,
		label: 'CAD Models',
		order,
		display: COLUMN_DISPLAY.TRUE
	});
}

function parsePrice(str) {
	if (typeof str !== 'string') return str;

	str = str.replace(/(,)/g, '');

	if (!isNaN(str) && !isNaN(parseFloat(str))) return Number(str);

	return str;
}

/* Columns processed for MUIDataTable */
export const selectMDTProcessedColumns = (state, displayModeIsWPN) => {
	const columns = state.categoryData.columns;
	const domain = window.location.origin;
	const fullURL = window.location.href;
	const path = window.location.pathname;
	if (columns[columns.length - 1].cid !== CIDS.RANK_WPN) {
		console.error(
			`Error: 'rankWPN' column must be on the last position in columns list to make ranking work properly.`
		);
		return;
	}

	return columns.map(col => {
		const defaultColumn = {
			label: col.label ? cleanHTMLtags(col.label) : col.label,
			name: col.cid,
			options: {
				customHeadLabelRender: columnMeta => {
					return RenderCustomHeadLabel(state.filters.appliedState, displayModeIsWPN, columns, columnMeta);
				},
				customBodyRenderLite: dataIndex => {
					return RenderCustomCellComponent(
						col.cid,
						state.categoryData.filteredProducts[dataIndex][col.cid],
						state.categoryData.filteredProducts[dataIndex][CIDS.PART_SUITABILITY],
						state.categoryData.filteredProducts[dataIndex],
						domain,
						fullURL,
						path
					);
				},
				display: col.display,
				download: col.display !== COLUMN_DISPLAY.EXCLUDED,
				searchable: col.cid !== CIDS.DATASHEET,
				setCellProps: displayModeIsWPN
					? (_cellValue, _rowIndex, _columnIndex) => {
							return {
								className: {},
								style: {
									borderBottom: 0
								}
							};
					  }
					: null,
				sort: displayModeIsWPN ? false : true,
				sortThirdClickReset: true
			}
		};

		switch (col.cid) {
			case CIDS.TOOLS:
				return {
					...defaultColumn,
					options: {
						...defaultColumn.options,
						sort: false,
						searchable: false,
						download: false
					}
				};
			case CIDS.PRICE:
				return {
					...defaultColumn,
					options: {
						...defaultColumn.options,
						sortCompare: order => {
							return (obj1, obj2) => {
								if (obj1.data === null || obj1.data === '-') return 1;
								if (obj2.data === null || obj2.data === '-') return -1;
								const price1 = parsePrice(obj1.data);
								const price2 = parsePrice(obj2.data);

								return (price1 - price2) * (order === SORT_DIRECTIONS.ASC ? 1 : -1);
							};
						}
					}
				};
			default:
				return defaultColumn;
		}
	});
};

export const getNumberOfProducts = state => state.categoryData.products.length;
export const getNumberOfProductGroups = state =>
	getUniquePropertyValues(state.categoryData.products, CIDS.GENERAL_PART_NUMBER).length;
export const getNumberOfFilteredProducts = state => state.categoryData.filteredProducts.length;

export const { filterProducts, resetStatus, updateColumnDisplay, changeExpandState } = categoryDataSlice.actions;
export default categoryDataSlice.reducer;
